import { useCallback, useRef } from "react";

export function useTextareaInsertion<T extends HTMLTextAreaElement>() {
  const cursorRef = useRef<{ start: number; end: number } | null>(null);

  // Save cursor before state update (React rerenders)
  const saveCursor = useCallback((el: T | null) => {
    if (el) {
      cursorRef.current = {
        start: el.selectionStart,
        end: el.selectionEnd,
      };
    }
  }, []);

  // Restore cursor AFTER React state update
  const restoreCursor = useCallback((el: T | null) => {
    if (!el || !cursorRef.current) return;

    requestAnimationFrame(() => {
      el.selectionStart = el.selectionEnd = cursorRef.current!.start;
      el.focus();
    });
  }, []);

  // Insert text at cursor into a controlled value
  const insertText = useCallback(
    (el: T, currentValue: string, insert: string) => {
      const start = el.selectionStart ?? currentValue.length;
      const end = el.selectionEnd ?? start;

      const newValue =
        currentValue.slice(0, start) + insert + currentValue.slice(end);

      // Save cursor for after render
      cursorRef.current = {
        start: start + insert.length,
        end: start + insert.length,
      };

      return newValue;
    },
    [],
  );

  return { saveCursor, restoreCursor, insertText };
}
